
import { defineComponent, toRefs, ref, computed } from 'vue'
import { useInfiniteQuery } from '@tanstack/vue-query'

import type { Maybe, Status, ActivityId } from '@/types'
import { PuzzleIcon } from '@icons'
import { SortDirection } from '@/services/BaseService.types'
import {
  useDebouncedRef,
  useActivityService,
  useActivity,
  useDefaultTags,
} from '@/use'
import { getCurrentInstance, indexBy } from '@/util'
import { paginator } from '@/modules/pagination/pagination.util'

import { SidebarEntity } from './Sidebar.types'
import BaseSidebarSection from './BaseSidebarSection.vue'
import SidebarList from './SidebarList.vue'
import SidebarListItem from './SidebarListItem.vue'
import SidebarTime from './SidebarTime.vue'

const fields = {
  id: true,
  name: true,
  updatedAt: true,
} as const

export default defineComponent({
  name: 'ActivitySidebarSection',
  components: {
    PuzzleIcon,
    BaseSidebarSection,
    SidebarList,
    SidebarListItem,
    SidebarTime,
  },
  props: {
    expanded: {
      type: Boolean,
      default: false,
    },
    open: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const { open } = toRefs(props)
    const { service } = useActivityService()
    const {
      create: createActivity,
      edit: editActivity,
      del: deleteActivity,
      copy: copyActivity,
    } = useActivity()
    const query = ref('')

    const { tags } = useDefaultTags('activity-search-tags')

    const status = ref<Maybe<Status>>(undefined)
    const debouncedQuery = useDebouncedRef(query)
    const limit = 25

    const { isLoading, isRefetching, data, refetch, fetchNextPage } =
      useInfiniteQuery({
        queryKey: ['activities', debouncedQuery, status, fields, tags, limit],
        queryFn: ({ pageParam }) =>
          service.search(
            {
              query: query.value,
              tags: tags.value,
              status: status.value,
              scoped: false,
            },
            fields,
            {
              limit,
              page: pageParam,
            },
            [
              {
                direction: SortDirection.DESC,
                field: 'updatedAt',
              },
            ]
          ),
        getNextPageParam: (page) => {
          return page.page === page.pageCount ? undefined : page.page + 1
        },
        enabled: open,
        initialPageParam: 1,
      })

    const loading = computed(() => isLoading.value || isRefetching.value)
    const activities = computed(() =>
      (data.value?.pages ?? []).map((page) => page.data).flat()
    )
    const activitiesById = computed(() => indexBy(activities.value, 'id'))

    function toggle() {
      emit('toggle', SidebarEntity.ACTIVITY)
    }

    const draggable = computed(() => {
      const { name } = instance.proxy.$route
      return name === 'group' || name === 'room'
    })

    async function create() {
      const res = await createActivity()
      if (res === null) {
        return
      }
      await refetch()
    }

    async function edit(id: ActivityId) {
      const index = activities.value.findIndex((item) => item.id === id)
      if (index === activities.value.length - 1) {
        await fetchNextPage()
      }
      const res = await editActivity(
        id,
        paginator(activities.value, id, edit, edit)
      )
      if (res === null) {
        return
      }
      const activity = activitiesById.value.get(id)
      if (activity === undefined) {
        return
      }
      activity.name = res.name
      activity.updatedAt = res.updatedAt
    }

    async function del(id: ActivityId) {
      const res = await deleteActivity(id)
      if (res === null) {
        return
      }
      await refetch()
    }

    async function copy(id: ActivityId, name: string) {
      const result = await copyActivity(id, name)
      if (result === null) return
      await refetch()
    }

    return {
      activities,
      loading,
      fetchNextPage,
      toggle,
      query,
      tags,
      status,
      create,
      edit,
      del,
      copy,
      draggable,
      SidebarEntity,
    }
  },
})
